// RideIntroModal
// ==============
//
// Wave 14ch: One-time intro overlay wenn der User zum ersten Mal RIDE
// drückt (entweder via Map-Button, "Save & ride now" auf CreateCustomPage,
// oder bei Tap auf einen Trail mit auto-start).
//
// Inhalt (kurz, klar, kein Roman):
//   - Headline: "Heads up before you ride"
//   - GPS-Pause-Warning: Display ausschalten = Aufzeichnung stoppt
//     (iOS/Android-Policy für PWAs)
//   - Pocket-Mode-Tipp: Akku schonen + Touch-Lock, im Profile-Menü zu
//     aktivieren, Handy in Hosentasche
//   - "Got it" Button + "Don't show again" Checkbox
//
// Persistierung der Dismissal: lib/rideIntro.ts (localStorage,
// pro-Gerät — sync'd bewusst NICHT zu anderen Geräten weil jeder
// neue Browser-Install den Hinweis einmal lesen soll).

import { useState } from 'react';
import { dismissRideIntroForever } from '../lib/rideIntro';

interface Props {
  /** Wird gerufen wenn der User "Got it" tippt. Caller proceeded dann mit
   *  dem eigentlichen Ride-Start. */
  onConfirm: () => void;
}

export function RideIntroModal({ onConfirm }: Props) {
  // Wave 14ch fix: Default UNgechecked — User muss bewusst entscheiden,
  // dass er den Hinweis nicht mehr sehen will. Sonst klickt jemand einmal
  // "Got it" und versteht erst nach der ersten verlorenen Track-Lücke
  // warum's wichtig war.
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleConfirm = () => {
    if (dontShowAgain) dismissRideIntroForever();
    onConfirm();
  };

  return (
    // z-[130] sitzt über InstallGate(110)/Onboarding(120) damit kein
    // anderer Gate ihn überdeckt. fixed inset-0 für klassisches Modal-
    // Backdrop, p-5 mobil-tauglich.
    <div
      className="fixed inset-0 z-[130] bg-black/60 backdrop-blur-sm flex items-center justify-center p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ride-intro-title"
    >
      <div className="max-w-sm w-full bg-turbo-900 border border-turbo-700/60 rounded-2xl shadow-2xl p-5">
        <h2
          id="ride-intro-title"
          className="text-xl font-bold text-sky-400 mb-3"
        >
          Heads up before you ride
        </h2>

        <p className="text-white/85 text-sm leading-relaxed mb-3">
          When your screen turns off, your phone pauses GPS recording —
          that's an iOS &amp; Android policy for web apps, not something we
          can override. If you lock the phone mid-ride, you'll get a gap in
          your track.
        </p>

        <p className="text-white/85 text-sm leading-relaxed mb-3">
          To save battery without losing GPS, turn on{' '}
          <span className="text-sky-300 font-semibold">Battery Safe Mode</span>{' '}
          (in your Profile menu) and slip the phone into your pocket with
          the screen still on. Battery Safe Mode dims the display and ignores
          accidental touches.
        </p>

        <label className="flex items-start gap-2.5 mt-4 mb-5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-turbo-500 bg-turbo-700 text-sky-500 focus:ring-sky-500/40"
          />
          <span className="text-white/70 text-sm">Don't show this again</span>
        </label>

        <button
          type="button"
          onClick={handleConfirm}
          className="w-full rounded-full bg-sky-500 text-white text-base font-semibold py-3 active:bg-sky-600"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
